// =============================================================================
// UTILITAIRES POUR LA GÉNÉRATION DES PDFs
// =============================================================================
// IMPORTANT: Tous les calculs de rentabilité sont effectués en HT
// La TVA est neutre pour l'entreprise (collectée puis reversée à l'État)
// =============================================================================

import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

// =============================================================================
// FORMATAGE DES MONTANTS
// =============================================================================

// Formater un montant en euros (avec décimales si nécessaire)
// IMPORTANT: Utilise un formatage manuel pour éviter les caractères d'espace spéciaux
// (les espaces insécables \u202F et \u00A0 de Intl.NumberFormat s'affichent comme "/" dans react-pdf)
export const formatMontant = (montant: number, forceDecimals = false): string => {
  const hasDecimals = montant % 1 !== 0;
  let numberStr: string;

  if (hasDecimals || forceDecimals) {
    // Avec décimales : arrondir à 2 chiffres
    const rounded = Math.round(montant * 100) / 100;
    const [entier, decimales] = rounded.toFixed(2).split('.');
    // Ajouter les espaces pour les milliers
    const entierFormate = entier.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    numberStr = entierFormate + ',' + decimales;
  } else {
    // Sans décimales : arrondir à l'entier
    const rounded = Math.round(montant);
    numberStr = rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  }

  return numberStr + ' €';
};

// Formater un montant avec mention HT explicite
// IMPORTANT: Formatage manuel pour éviter les "/" dans react-pdf
export const formatMontantHT = (montant: number): string => {
  const hasDecimals = montant % 1 !== 0;
  let numberStr: string;

  if (hasDecimals) {
    const rounded = Math.round(montant * 100) / 100;
    const [entier, decimales] = rounded.toFixed(2).split('.');
    const entierFormate = entier.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    numberStr = entierFormate + ',' + decimales;
  } else {
    const rounded = Math.round(montant);
    numberStr = rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  }

  return numberStr + ' € HT';
};

// Formater un montant avec mention TTC explicite
// IMPORTANT: Formatage manuel pour éviter les "/" dans react-pdf
export const formatMontantTTC = (montant: number): string => {
  const hasDecimals = montant % 1 !== 0;
  let numberStr: string;

  if (hasDecimals) {
    const rounded = Math.round(montant * 100) / 100;
    const [entier, decimales] = rounded.toFixed(2).split('.');
    const entierFormate = entier.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    numberStr = entierFormate + ',' + decimales;
  } else {
    const rounded = Math.round(montant);
    numberStr = rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  }

  return numberStr + ' € TTC';
};

// Formater un montant compact (1.5k €)
export const formatMontantCompact = (montant: number): string => {
  if (Math.abs(montant) >= 1000) {
    return (montant / 1000).toFixed(1).replace('.', ',') + 'k €';
  }
  return formatMontant(montant);
};

// Formater un montant compact avec mention HT
export const formatMontantCompactHT = (montant: number): string => {
  if (Math.abs(montant) >= 1000) {
    return (montant / 1000).toFixed(1).replace('.', ',') + 'k € HT';
  }
  return formatMontantHT(montant);
};

// Formater une date en français
export const formatDate = (dateStr: string | Date): string => {
  try {
    const date = typeof dateStr === 'string' ? new Date(dateStr) : dateStr;
    return format(date, 'd MMM yyyy', { locale: fr });
  } catch {
    return String(dateStr);
  }
};

// Formater une date courte (09/12)
export const formatDateCourt = (dateStr: string | Date): string => {
  try {
    const date = typeof dateStr === 'string' ? new Date(dateStr) : dateStr;
    return format(date, 'dd/MM', { locale: fr });
  } catch {
    return String(dateStr);
  }
};

// Formater un pourcentage
export const formatPct = (value: number, showSign = false): string => {
  const sign = showSign && value > 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
};

// Obtenir l'icône d'écart
export const getEcartIcon = (ecart: number, inverse = false): string => {
  const isGood = inverse ? ecart > 0 : ecart <= 0;
  if (Math.abs(ecart) < 1) return '-';
  return isGood ? '+' : '!';
};

// Calculer les jours écoulés depuis une date
export const calculerJoursEcoules = (dateDebut: string | Date): number => {
  try {
    const debut = typeof dateDebut === 'string' ? new Date(dateDebut) : dateDebut;
    const aujourdhui = new Date();
    const diffTime = aujourdhui.getTime() - debut.getTime();
    return Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
  } catch {
    return 0;
  }
};

// Calculer la durée entre deux dates
export const calculerDuree = (dateDebut: string | Date, dateFin: string | Date): number => {
  try {
    const debut = typeof dateDebut === 'string' ? new Date(dateDebut) : dateDebut;
    const fin = typeof dateFin === 'string' ? new Date(dateFin) : dateFin;
    const diffTime = fin.getTime() - debut.getTime();
    return Math.max(1, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
  } catch {
    return 1;
  }
};

// =============================================================================
// TYPES POUR LES PDFs
// =============================================================================

export interface ChantierPDFData {
  nom: string;
  client: string;
  adresse: string;
  reference?: string;
  date_debut: string;
  date_fin_prevue: string;
  date_fin_initiale?: string; // Date de fin initiale (avant retard)
  date_fin_reelle?: string;
  duree_prevue: number;
  statut: string;
}

export interface DevisPDFData {
  montant_ht: number;
  montant_ttc: number;
  tva: number;
  version: string;
  statut: string;
}

export interface FacturePDFData {
  fournisseur: string;
  categorie: string;
  montant_ht: number;
  date: string;
}

export interface FraisPDFData {
  type: string;
  description: string;
  montant: number;
  date: string;
}

export interface MembrePDFData {
  nom: string;
  poste: string;
  jours: number;
  heures: number;
  cout: number;
}

export interface DepensesPDFData {
  factures: FacturePDFData[];
  frais: FraisPDFData[];
  total_factures: number;
  total_frais: number;
  total_mo: number;
  total: number;
}

export interface EquipePDFData {
  membres: MembrePDFData[];
  total_jours: number;
  total_heures: number;
  total_cout: number;
  cout_horaire_moyen: number;
}

export interface FacturationPDFData {
  total_facture: number;
  total_paye: number;
  reste_a_facturer: number;
}

export interface MetriquesPDFData {
  marge_euros: number;
  marge_pct: number;
  roi: number;
  ca_jour: number;
  rentabilite_horaire: number;
  statut_rentabilite: 'VERT' | 'JAUNE' | 'ORANGE' | 'ROUGE';
  budget_disponible: number;
  cout_journalier: number;
  // Données de retard
  jours_retard_travailles?: number;
  cout_retard?: number;
  // Données de reprise (après suspension)
  jours_reprise_travailles?: number;
  cout_reprise?: number;
}

export interface BenchmarksPDFData {
  marge_min: number;
  marge_max: number;
  marge_moyenne: number;
}

export interface AnalyseIAPDFData {
  score: number;
  verdict: string;
  points_forts: string[];
  alertes: string[];
  recommandations: string[];
  conseil_expert: string;
}

export interface SuspensionPDFData {
  date_suspension: string;
  date_reprise: string | null;
  duree_jours: number;
  raison: string;
  type_suspension: 'totale' | 'partielle';
  cout_mobilisation: number;
}

export interface SuspensionsPDFData {
  suspensions: SuspensionPDFData[];
  total_jours: number;
  total_cout: number;
  nb_suspensions: number;
}

// Interface principale pour les données PDF
export interface PDFData {
  chantier: ChantierPDFData;
  devis: DevisPDFData;
  depenses: DepensesPDFData;
  equipe: EquipePDFData;
  facturation: FacturationPDFData;
  metriques: MetriquesPDFData;
  benchmarks: BenchmarksPDFData;
  analyseIA?: AnalyseIAPDFData;
  suspensions?: SuspensionsPDFData;
}

// =============================================================================
// FONCTIONS DE CALCUL
// =============================================================================

// Calculer l'avancement en pourcentage
export const calculerAvancement = (joursEcoules: number, dureePrevue: number): number => {
  if (dureePrevue <= 0) return 0;
  return Math.min(100, Math.round((joursEcoules / dureePrevue) * 100));
};

// Calculer l'écart jours
export const calculerEcartJours = (joursEcoules: number, dureePrevue: number): number => {
  return joursEcoules - dureePrevue;
};

// Obtenir le statut planning
export const getStatutPlanning = (ecartJours: number): { label: string; emoji: string; color: 'vert' | 'orange' | 'rouge' } => {
  if (ecartJours <= 0) return { label: 'En avance', emoji: '', color: 'vert' };
  if (ecartJours <= 2) return { label: 'Dans les temps', emoji: '', color: 'vert' };
  if (ecartJours <= 5) return { label: 'Léger retard', emoji: '', color: 'orange' };
  return { label: 'Retard important', emoji: '', color: 'rouge' };
};

// Calculer la ventilation des coûts en pourcentages
export const calculerVentilation = (mo: number, materiaux: number, frais: number): { mo: number; materiaux: number; frais: number } => {
  const total = mo + materiaux + frais;
  if (total === 0) return { mo: 0, materiaux: 0, frais: 0 };
  return {
    mo: Math.round((mo / total) * 100),
    materiaux: Math.round((materiaux / total) * 100),
    frais: Math.round((frais / total) * 100),
  };
};

// Générer les alertes automatiques
export const genererAlertes = (data: PDFData): string[] => {
  const alertes: string[] = [];

  // Marge trop basse
  if (data.metriques.marge_pct < 10) {
    alertes.push(`Marge faible (${formatPct(data.metriques.marge_pct)}) - Surveiller les couts`);
  }

  // Retard planning - utiliser les jours de retard déclarés (affectations)
  const joursRetardDeclares = data.metriques.jours_retard_travailles || 0;
  if (joursRetardDeclares > 0) {
    alertes.push(`Retard de ${joursRetardDeclares} jour${joursRetardDeclares > 1 ? 's' : ''} declare`);
  }

  // Statut facturation client (utiliser les vraies données)
  const { total_facture, total_paye, reste_a_facturer } = data.facturation;
  const devisMontant = data.devis.montant_ht;

  if (devisMontant > 0) {
    // Cas 1 : Rien facturé
    if (total_facture === 0) {
      alertes.push(`Facturation client : Aucune facture emise (devis ${formatMontant(devisMontant)})`);
    }
    // Cas 2 : Facturé partiellement
    else if (reste_a_facturer > 0) {
      alertes.push(`Facturation client : ${formatMontant(total_facture)} facture sur ${formatMontant(devisMontant)} (reste ${formatMontant(reste_a_facturer)})`);
    }

    // Statut paiement
    if (total_facture > 0) {
      if (total_paye === 0) {
        alertes.push(`Paiement client : Aucun paiement recu (${formatMontant(total_facture)} en attente)`);
      } else if (total_paye < total_facture) {
        const resteAPayer = total_facture - total_paye;
        alertes.push(`Paiement client : ${formatMontant(total_paye)} encaisse sur ${formatMontant(total_facture)} (reste ${formatMontant(resteAPayer)})`);
      }
      // Si total_paye >= total_facture, pas d'alerte (tout est payé)
    }
  }

  // Budget dépassé
  if (data.metriques.budget_disponible < 0) {
    alertes.push(`Budget depasse de ${formatMontant(Math.abs(data.metriques.budget_disponible))}`);
  }

  return alertes;
};

// Générer le résumé RAS si pas d'alertes
export const genererResume = (alertes: string[]): string => {
  if (alertes.length === 0) {
    return 'RAS - Chantier sous contrôle';
  }
  return '';
};
